import { QuickAddComponent } from '@theme/quick-add';
import { addToCartAndOpenDrawer } from '@theme/cart-quick-add';

/**
 * Lazily-created, shared `quick-add-component` instance used to drive the global
 * quick-add modal from the collection grid slider. The slider isn't wrapped in a
 * product-card, so it can't rely on the component's automatic URL resolution;
 * instead we feed it an explicit product URL via `openForUrl`.
 *
 * @type {QuickAddComponent | null}
 */
let sharedQuickAdd = null;

/**
 * @returns {Promise<QuickAddComponent>}
 */
async function getQuickAdd() {
  await customElements.whenDefined('quick-add-component');

  if (!sharedQuickAdd) {
    sharedQuickAdd = /** @type {QuickAddComponent} */ (document.createElement('quick-add-component'));
    sharedQuickAdd.hidden = true;
    document.body.appendChild(sharedQuickAdd);
  }

  return sharedQuickAdd;
}

/**
 * A product slider that acts as a variant pre-selector.
 *
 * Each slide carries a `data-product-url` and a fallback `data-variant-id`. The
 * size buttons (`.grid-slider-options__value`) toggle the selected variant for
 * their slide.
 *
 * - Default mode (the `enable_quick_add_dialog` setting is on, so the host
 *   element has no `data-direct-add` attribute): clicking a size button only
 *   preselects it via `aria-pressed`; clicking `.slider-add` opens the shared
 *   quick-add modal showing the active slide's product, pre-selected to the
 *   chosen variant.
 * - Direct-add mode (`data-direct-add` present on the host, i.e. the quick-add
 *   dialog setting is off, so `.slider-add` is not rendered): clicking a size
 *   button resolves the exact variant for the clicked size (this template has
 *   no color options, so color is always `null`) using the slide's
 *   `[data-ct-variant-map]` JSON, then adds it straight to the cart and opens
 *   the cart drawer.
 */
class QuickAddSlider extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    const optionButton = target.closest('.grid-slider-options__value');
    if (optionButton instanceof HTMLButtonElement) {
      this.#selectOption(optionButton);
      return;
    }

    if (target.closest('.slider-add')) {
      this.#openModal();
    }
  };

  /**
   * Marks the clicked size button as the selected variant for its slide.
   *
   * - Default mode (no `data-direct-add` on the host): preserves the exact
   *   prior behavior — just toggles `aria-pressed`.
   * - Direct-add mode (`data-direct-add` present on the host): resolves the
   *   exact variant for the clicked size (color is always `null` here, since
   *   this template has no color options) using the slide's
   *   `[data-ct-variant-map]` JSON, then adds it straight to the cart and
   *   opens the cart drawer instead of merely preselecting it.
   *
   * @param {HTMLButtonElement} button
   * @returns {Promise<void>}
   */
  async #selectOption(button) {
    const group = button.closest('.grid-slider-options');
    if (!group) return;

    for (const option of group.querySelectorAll('.grid-slider-options__value')) {
      option.setAttribute('aria-pressed', 'false');
    }
    button.setAttribute('aria-pressed', 'true');

    if (!this.hasAttribute('data-direct-add')) return;

    // Direct-add mode: resolve the exact variant for the selected color (if
    // any) + clicked size, then add it to the cart and open the drawer.
    const slide = /** @type {HTMLElement | null} */ (
      button.closest('.grid-slider-card') || this.#getActiveSlide()
    );
    if (!slide) return;

    const pressedColor = /** @type {HTMLElement | null} */ (
      slide.querySelector('[data-color][aria-pressed="true"]')
    );
    const color = pressedColor?.dataset.color || null;
    const size = button.dataset.size;

    /** @type {string | null | undefined} */
    let variantId = button.dataset.variantId;

    const mapScript = slide.querySelector('[data-ct-variant-map]');
    if (mapScript) {
      try {
        const map = JSON.parse(mapScript.textContent ?? '[]');
        const variant = map.find(
          (/** @type {{ id: number, available: boolean, options: string[] }} */ v) =>
            (!color || v.options.includes(color)) && v.options.includes(size)
        );
        variantId = variant && variant.available ? variant.id : variant ? null : button.dataset.variantId;
      } catch {
        // Malformed/missing variant map — fall back to the button's own data-variant-id.
      }
    }

    if (!variantId) return;

    await addToCartAndOpenDrawer(variantId, { sourceId: 'quick-add-slider' });
  }

  /**
   * @returns {HTMLElement | null} The active slide, or the first slide as fallback.
   */
  #getActiveSlide() {
    return this.querySelector('.swiper-slide-active') ?? this.querySelector('.grid-slider-card');
  }

  /**
   * Resolves the variant id for a slide: the pressed size button, otherwise the
   * slide's default variant.
   * @param {HTMLElement} slide
   * @returns {string}
   */
  #getSelectedVariantId(slide) {
    const pressed = /** @type {HTMLElement | null} */ (
      slide.querySelector('.grid-slider-options__value[aria-pressed="true"]')
    );
    return pressed?.dataset.variantId || slide.dataset.variantId || '';
  }

  async #openModal() {
    const slide = this.#getActiveSlide();
    if (!slide || !slide.dataset.productUrl) return;

    const url = new URL(slide.dataset.productUrl, window.location.origin);
    const variantId = this.#getSelectedVariantId(slide);
    if (variantId) url.searchParams.set('variant', variantId);

    const quickAdd = await getQuickAdd();
    await quickAdd.openForUrl(url.toString());
  }
}

if (!customElements.get('quick-add-slider-component')) {
  customElements.define('quick-add-slider-component', QuickAddSlider);
}
